import { createImage } from './CropUtils';

/*
 * Otimização de fotos antes do upload. Foto de celular costuma vir com 4000 px e 6 a 12 MB:
 * passaria do limite do backend (5 MB) e deixaria a página pública lenta no 4G. Reduzimos no
 * próprio navegador para um tamanho que ainda fica nítido numa tela grande.
 */

const DEFAULT_MAX_DIMENSION = 2400;
const DEFAULT_MAX_BYTES = 5 * 1024 * 1024;
// Abaixo disso a foto não precisa ser mexida, mesmo que tenha muitos pixels.
const LIGHT_FILE_BYTES = 1.5 * 1024 * 1024;
const QUALITY_STEPS = [0.86, 0.78, 0.7];

const canvasToBlob = (canvas, type, quality) =>
    new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const replaceExtension = (fileName, type) => {
    const extension = type === 'image/webp' ? 'webp' : 'jpg';
    const baseName = (fileName || 'foto').replace(/\.[^.]+$/, '');
    return `${baseName}.${extension}`;
};

/** Lê largura e altura reais da imagem (já com a rotação EXIF aplicada pelo navegador). */
export const readImageDimensions = async (file) => {
    const objectUrl = URL.createObjectURL(file);
    try {
        const image = await createImage(objectUrl);
        return { width: image.naturalWidth, height: image.naturalHeight };
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
};

/**
 * Devolve a foto pronta para envio: a original, quando já é leve e de tamanho razoável, ou uma
 * versão reduzida (lado maior até maxDimension) em WEBP. O Safari não gera WEBP pelo canvas e
 * devolve PNG; nesse caso caímos para JPEG, com fundo branco porque JPEG não tem transparência.
 *
 * @returns {Promise<{file: File, width: number, height: number, optimized: boolean, originalSize: number}>}
 */
export const optimizeImageForUpload = async (file, { maxDimension = DEFAULT_MAX_DIMENSION, maxBytes = DEFAULT_MAX_BYTES } = {}) => {
    const objectUrl = URL.createObjectURL(file);
    try {
        const image = await createImage(objectUrl);
        const width = image.naturalWidth;
        const height = image.naturalHeight;
        const largestSide = Math.max(width, height);

        if (file.size <= LIGHT_FILE_BYTES || (file.size <= maxBytes && largestSide <= maxDimension)) {
            return { file, width, height, optimized: false, originalSize: file.size };
        }

        const ratio = Math.min(1, maxDimension / largestSide);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width * ratio);
        canvas.height = Math.round(height * ratio);
        const context = canvas.getContext('2d');
        context.imageSmoothingQuality = 'high';
        context.drawImage(image, 0, 0, canvas.width, canvas.height);

        let blob = null;
        for (const quality of QUALITY_STEPS) {
            blob = await canvasToBlob(canvas, 'image/webp', quality);
            if (!blob || blob.type !== 'image/webp') {
                // Sem WEBP no navegador: redesenha sobre branco e gera JPEG.
                context.globalCompositeOperation = 'destination-over';
                context.fillStyle = '#ffffff';
                context.fillRect(0, 0, canvas.width, canvas.height);
                context.globalCompositeOperation = 'source-over';
                blob = await canvasToBlob(canvas, 'image/jpeg', quality);
            }
            if (blob && blob.size <= maxBytes) {
                break;
            }
        }

        // Se a conversão não ganhou nada e a original já cabe no limite, mantém a original.
        if (!blob || (blob.size >= file.size && file.size <= maxBytes)) {
            return { file, width, height, optimized: false, originalSize: file.size };
        }

        const optimizedFile = new File([blob], replaceExtension(file.name, blob.type), { type: blob.type });
        return { file: optimizedFile, width: canvas.width, height: canvas.height, optimized: true, originalSize: file.size };
    } finally {
        URL.revokeObjectURL(objectUrl);
    }
};
